// Shared bits for the dashboard overview components. Dates are 'YYYY-MM-DD'
// strings straight from the API; they're parsed as local midnight so the
// weekday label never shifts.
//
// Visual rules: one series hue (theme primary), status colours only where a
// colour means good/bad, one hero number per view, thin marks, hairline grid.
// Theme spacing is 4px.
import { alpha } from '@mui/material';

export const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'lastweek', label: 'Last week' },
  { value: 'month', label: 'This month' },
  { value: 'lastmonth', label: 'Last month' },
];

// Status palette (fixed, never themed): good / warning / serious / critical.
export const COLORS = {
  good: '#0ca30c',
  warn: '#fab219',
  serious: '#ec835a',
  bad: '#d03b3b',
  muted: '#898781',
};

// The hero band: a deep blue gradient panel the top of the page sits on. White
// ink, one glow in the corner. Same in light and dark mode so the page always
// has one strong focal point.
export const BAND = {
  bg: '#132a5c',                       // mid-point of the gradient; the "surface" for rings on it
  text: '#ffffff',
  muted: 'rgba(255,255,255,0.68)',
  faint: 'rgba(255,255,255,0.45)',
  line: 'rgba(255,255,255,0.16)',
  track: 'rgba(255,255,255,0.14)',
  accent: '#8ec1ff',                   // the series hue, lifted so it reads on navy
  glass: 'rgba(255,255,255,0.08)',
  glassBorder: 'rgba(255,255,255,0.14)',
};

// Ink/line/surface tokens a chart draws with: the theme's, or the band's.
export const toneOf = (theme, onBand = false) => (onBand
  ? { text: BAND.text, muted: BAND.muted, line: BAND.line, surface: BAND.bg, accent: BAND.accent, track: BAND.track, bad: '#ff7b7b', serious: '#ffa26b', good: '#5fd37a', warn: '#ffd166' }
  : { text: theme.palette.text.primary, muted: theme.palette.text.secondary, line: hairline(theme), surface: theme.palette.background.paper, accent: theme.palette.primary.main, track: alpha(theme.palette.text.primary, 0.06), bad: COLORS.bad, serious: COLORS.serious, good: COLORS.good, warn: COLORS.warn });

// Sequential steps of the series hue for a 0–1 share: light -> dark, one hue.
// Dark mode gets its own steps from the same hue, chosen against the dark surface.
export const SEQ_STEPS = ['#e8f1ff', '#bfd8ff', '#7fb1ff', '#3b82f6', '#1d4ed8', '#173ea6'];
export const SEQ_STEPS_DARK = ['#242a36', '#1f3561', '#234d9a', '#2f6fe0', '#6ea3ff', '#a9ccff'];
const seqIndex = (share, steps) => {
  const v = Math.max(0, Math.min(1, Number(share) || 0));
  return v === 0 ? 0 : Math.min(steps.length - 1, 1 + Math.floor(v * (steps.length - 1)));
};
export const seqColor = (share, dark = false) => {
  const steps = dark ? SEQ_STEPS_DARK : SEQ_STEPS;
  return steps[seqIndex(share, steps)];
};
export const captionSx = {
  fontSize: '0.8125rem',
  color: 'text.secondary',
  lineHeight: 1.4,
};

export const hairline = (theme) => (theme.palette.mode === 'dark' ? '#2c2c2a' : '#e5e7eb');

const toLocal = (iso) => new Date(`${iso}T00:00:00`);

export const fmtHours = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(1).replace(/\.0$/, '');
};

export const fmtInt = (n) => (Number(n) || 0).toLocaleString();

// 1,284 / 12.9K — for stat tiles.
export const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return fmtInt(Math.round(v));
};

export const dayLabel = (iso, opts = { weekday: 'short', day: 'numeric' }) =>
  toLocal(iso).toLocaleDateString(undefined, opts);

export const rangeLabel = (start, end) =>
  start === end
    ? dayLabel(start, { weekday: 'long', day: 'numeric', month: 'long' })
    : `${dayLabel(start, { weekday: 'short', day: 'numeric', month: 'short' })} – ${dayLabel(end, { weekday: 'short', day: 'numeric', month: 'short' })}`;

export const periodWord = (period) => ({
  today: 'today', week: 'this week', lastweek: 'last week', month: 'this month', lastmonth: 'last month',
}[period] || 'this week');

// "just now", "35 min ago", "3 h ago", "yesterday", "12 Sep" — for the activity stream.
export const relativeTime = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  if (diff < 172800) return 'yesterday';
  if (diff < 7 * 86400) return `${Math.round(diff / 86400)} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export const scanTooltip = (d) => {
  if (!d.hasEmployeeId) return 'no Employee ID, so no scan data';
  if (d.scannedHours == null) return 'no scan recorded';
  return `scanned in ${d.firstIn} – ${d.lastOut} (${fmtHours(d.scannedHours)} h)`;
};

// ---- Soft style -------------------------------------------------------------
// Pastel tints: a light fill plus an ink that reads on it. Dark mode uses a
// translucent wash of the ink on the dark surface instead of a pastel.
const TINTS = {
  lavender: { bg: '#eceeff', ink: '#4f46e5', darkInk: '#a5b4fc' },
  sky:      { bg: '#e6f2ff', ink: '#1d4ed8', darkInk: '#93c5fd' },
  mint:     { bg: '#e3f7ec', ink: '#15803d', darkInk: '#86efac' },
  peach:    { bg: '#fff0e3', ink: '#c2410c', darkInk: '#fdba74' },
  rose:     { bg: '#fde8e8', ink: '#b91c1c', darkInk: '#fca5a5' },
  lemon:    { bg: '#fff7d6', ink: '#a16207', darkInk: '#fde047' },
  slate:    { bg: '#eef0f3', ink: '#475569', darkInk: '#cbd5e1' },
};
export const tint = (theme, name) => {
  const t = TINTS[name] || TINTS.slate;
  return theme.palette.mode === 'dark'
    ? { bg: alpha(t.darkInk, 0.14), ink: t.darkInk, border: alpha(t.darkInk, 0.2) }
    : { bg: t.bg, ink: t.ink, border: alpha(t.ink, 0.1) };
};

// Soft card: no border, big radius, a gentle shadow. `fill` is a tint name.
export const softSx = (theme, fill) => ({
  bgcolor: fill ? tint(theme, fill).bg : 'background.paper',
  borderRadius: 5,
  p: { xs: 4, md: 5 },
  border: theme.palette.mode === 'dark' ? `1px solid ${alpha('#fff', 0.06)}` : 'none',
  boxShadow: theme.palette.mode === 'dark' || fill ? 'none' : '0 1px 2px rgba(16,24,40,0.03), 0 8px 24px rgba(16,24,40,0.05)',
});

// The greeting panel: a soft diagonal pastel gradient.
export const heroSoftSx = (theme) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 4,
  px: { xs: 3.5, md: 4.5 },
  py: { xs: 2.5, md: 2.75 },
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(120deg, rgba(99,102,241,0.22) 0%, rgba(59,130,246,0.16) 55%, rgba(16,185,129,0.14) 100%)'
    : 'linear-gradient(120deg, #fde9dc 0%, #ebe7ff 48%, #dcf3ff 100%)',
  border: theme.palette.mode === 'dark' ? `1px solid ${alpha('#fff', 0.08)}` : 'none',
});

export const initials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
